import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { MDButton } from '../../components/MDButton';
import { MDTypography } from '../../components/MDTypography';
import { MDInput } from '../../components/MDInput';
import { Bill } from './ClaimDetailsTypes';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 450,
      maxWidth: 600,
      padding: theme.spacing(2),
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(2),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 200,
  },
  billInfo: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  billInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  billInfoValue: {
    fontWeight: 500,
  },
  radioGroup: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  customReasonBox: {
    marginTop: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  requiredText: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontStyle: 'italic',
    marginTop: theme.spacing(1),
  },
}));

interface RejectionModalProps {
  open: boolean;
  bill: Bill | null;
  claimId: string;
  loading: boolean;
  onClose: () => void;
  onReject: (billId: number, reason: string) => void;
}

export const RejectionModal: React.FC<RejectionModalProps> = ({
  open,
  bill,
  claimId,
  loading,
  onClose,
  onReject,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const predefinedReasons = [
    'Coverage limit reached.',
    'Pre-existing condition coverage limit reached or no coverage',
    'Not covered under policy terms and conditions',
    'No prior authorization granted',
    'Other'
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  useEffect(() => {
    if (open && bill) {
      // Reset form when modal opens
      setSelectedReason('');
      setCustomReason('');
    }
  }, [open, bill]);

  const handleReject = () => {
    if (!bill) return;
    
    let finalReason = selectedReason;
    if (selectedReason === 'Other') {
      if (!customReason.trim()) return;
      finalReason = customReason.trim();
    }
    
    if (!finalReason) return;
    onReject(bill.id, finalReason);
  };

  const isFormValid = () => {
    if (!selectedReason) return false;
    if (selectedReason === 'Other') {
      return customReason.trim().length > 0;
    }
    return true;
  };

  if (!bill) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.title}>
        <MDTypography variant="h6" fontWeight="medium">
          Reject Bill - {bill.gemBillNo}
        </MDTypography>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        <Box className={classes.billInfo}>
          <div className={classes.billInfoRow}>
            <MDTypography variant="body2" color="text">
              Bill ID:
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {bill.gemBillNo}
            </MDTypography>
          </div>
          <div className={classes.billInfoRow}>
            <MDTypography variant="body2" color="text">
              Provider:
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {bill.payableTo}
            </MDTypography>
          </div>
          <div className={classes.billInfoRow}>
            <MDTypography variant="body2" color="text">
              Bill Amount:
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {formatCurrency(bill.billedCharges)}
            </MDTypography>
          </div>
        </Box>

        <MDTypography variant="body2" fontWeight="medium" style={{ marginBottom: '12px' }}>
          Please select a rejection reason:
        </MDTypography>

        <RadioGroup
          value={selectedReason}
          onChange={(e) => setSelectedReason(e.target.value)}
          className={classes.radioGroup}
        >
          {predefinedReasons.map((reason) => (
            <FormControlLabel
              key={reason}
              value={reason}
              control={<Radio color="primary" />}
              label={
                <MDTypography variant="body2">
                  {reason}
                </MDTypography>
              }
            />
          ))}
        </RadioGroup>

        {selectedReason === 'Other' && (
          <Box className={classes.customReasonBox}>
            <MDInput
              label="Custom Rejection Reason"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Please provide a detailed reason for rejecting this bill..."
              error={selectedReason === 'Other' && !customReason.trim()}
            />
            {selectedReason === 'Other' && !customReason.trim() && (
              <MDTypography variant="caption" color="error" style={{ marginTop: '4px', display: 'block' }}>
                Custom reason is required when &ldquo;Other&rdquo; is selected
              </MDTypography>
            )}
          </Box>
        )}

        <MDTypography variant="caption" color="text" style={{ fontStyle: 'italic', marginTop: '12px' }}>
          * Please select a rejection reason before you can reject this bill
        </MDTypography>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="error"
          onClick={handleReject}
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Processing...' : 'Reject'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default RejectionModal;
